// Geo dataset built by tools/data/build.mjs from Natural Earth v5.1.2 (public domain; provenance in
// data/sources.json). Validated once at module load: bad data throws ContentValidationError.

import citiesJson from '../data/cities.json'
import landmaskJson from '../data/landmask.json'
import worldNetworkJson from '../data/world-network.json'
import { ContentValidationError } from './errors'

declare const cityIdBrand: unique symbol
declare const countryCodeBrand: unique symbol
/** Natural Earth NE_ID of a populated place. */
export type CityId = number & { readonly [cityIdBrand]: true }
/** Natural Earth ADM0_A3 country code. */
export type CountryCode = string & { readonly [countryCodeBrand]: true }

export interface City {
  readonly id: CityId
  readonly name: string
  readonly country: CountryCode
  readonly lat: number
  readonly lon: number
  readonly pop: number
  readonly capital: boolean
}

export interface Country {
  readonly code: CountryCode
  readonly name: string
  readonly capital: City
  /** Capital first, then by population descending. */
  readonly cities: readonly City[]
}

/** GDD §4: each belligerent needs at least this many cities. */
export const MIN_CITIES_PER_BELLIGERENT = 6

function fail(message: string): never {
  throw new ContentValidationError(`geo content: ${message}`)
}

function validLatLon(lat: number, lon: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
}

function loadCities(): readonly City[] {
  const ids = new Set<number>()
  return citiesJson.cities.map((raw): City => {
    if (!validLatLon(raw.lat, raw.lon)) fail(`city ${raw.id} has invalid coordinates`)
    if (raw.name.length === 0) fail(`city ${raw.id} has no name`)
    if (ids.has(raw.id)) fail(`duplicate city id ${raw.id}`)
    ids.add(raw.id)
    return { ...raw, id: raw.id as CityId, country: raw.country as CountryCode }
  })
}

/** Every city of the dataset in cities.json order: countries by code, each country's capital first, then by population. */
export const ALL_CITIES: readonly City[] = loadCities()

/** Every city of the dataset by its CityId: the one id lookup (content loaders, the sim's land library). */
export const CITY_BY_ID: ReadonlyMap<CityId, City> = new Map(ALL_CITIES.map((city) => [city.id, city]))

function loadCountries(): ReadonlyMap<CountryCode, Country> {
  const countries = new Map<CountryCode, Country>()
  for (const raw of citiesJson.countries) {
    const cities = raw.cityIds.map((id) => CITY_BY_ID.get(id as CityId) ?? fail(`country ${raw.code} references unknown city ${id}`))
    const capital = cities[0]
    if (!capital || capital.id !== raw.capitalId || !capital.capital) fail(`country ${raw.code} must list its capital first`)
    if (cities.some((c) => c.country !== raw.code)) fail(`country ${raw.code} lists a foreign city`)
    countries.set(raw.code as CountryCode, { code: raw.code as CountryCode, name: raw.name, capital, cities })
  }
  return countries
}

export const COUNTRIES: ReadonlyMap<CountryCode, Country> = loadCountries()

/**
 * World War edge candidates (tools/data/build.mjs): the spherical Delaunay triangulation of ALL_CITIES, as index pairs
 * [a, b] into ALL_CITIES with a < b, ascending. No two candidates cross (the build verifies it), so any subset is a
 * planar network, and together they connect every city.
 */
function loadWorldNetwork(): readonly (readonly [number, number])[] {
  const indexById = new Map<number, number>(ALL_CITIES.map((city, index) => [city.id, index]))
  const indexOf = (id: number): number => indexById.get(id) ?? fail(`world network references unknown city ${id}`)
  const pairs = worldNetworkJson.pairs.map((raw): readonly [number, number] => {
    const [idA, idB] = raw
    if (raw.length !== 2 || idA === undefined || idB === undefined) fail('world network pair is not two city ids')
    const a = indexOf(idA)
    const b = indexOf(idB)
    if (!(a < b)) fail(`world network pair ${idA}–${idB} is not in cities.json order`)
    return [a, b]
  })
  for (let i = 1; i < pairs.length; i++) {
    const [a0, b0] = pairs[i - 1] as readonly [number, number]
    const [a1, b1] = pairs[i] as readonly [number, number]
    if (!(a0 < a1 || (a0 === a1 && b0 < b1))) fail('world network pairs are not strictly ascending')
  }
  return pairs
}

export const WORLD_NETWORK_CANDIDATES: readonly (readonly [number, number])[] = loadWorldNetwork()

/** Countries that can fight a war (GDD §11 skirmish roster), sorted by name. */
export const PLAYABLE_COUNTRIES: readonly Country[] = [...COUNTRIES.values()]
  .filter((c) => c.cities.length >= MIN_CITIES_PER_BELLIGERENT)
  .sort((a, b) => a.name.localeCompare(b.name))

// ------------------------------------------------------------------------------------------------
// Land mask: equirectangular bitset, row 0 = northernmost band, column 0 = lon -180.

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

function decodeBase64(text: string): Uint8Array {
  const clean = text.replace(/=+$/, '')
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4))
  let buffer = 0
  let bits = 0
  let index = 0
  for (const char of clean) {
    const value = BASE64_ALPHABET.indexOf(char)
    if (value < 0) fail('landmask contains invalid base64')
    buffer = (buffer << 6) | value
    bits += 6
    if (bits >= 8) {
      bits -= 8
      out[index++] = (buffer >> bits) & 0xff
    }
  }
  return out
}

export class LandMask {
  private readonly bits: Uint8Array

  constructor(
    readonly cellDeg: number,
    readonly width: number,
    readonly height: number,
    base64Bits: string,
  ) {
    this.bits = decodeBase64(base64Bits)
    if (width * cellDeg !== 360 || height * cellDeg !== 180) fail('landmask dimensions do not cover the globe')
    if (this.bits.length !== (width * height) / 8) fail('landmask bit count does not match its dimensions')
  }

  isLand(lat: number, lon: number): boolean {
    return this.isLandCell(Math.floor((90 - lat) / this.cellDeg), Math.floor((lon + 180) / this.cellDeg))
  }

  /** Row 0 is the northernmost band, column 0 starts at 180°W. Rows clamp to the poles; columns wrap around the globe. */
  isLandCell(row: number, col: number): boolean {
    const r = Math.min(this.height - 1, Math.max(0, row))
    const c = ((col % this.width) + this.width) % this.width
    const index = r * this.width + c
    return ((this.bits[index >> 3] ?? 0) & (1 << (index & 7))) !== 0
  }
}

export const LAND_MASK = new LandMask(landmaskJson.cellDeg, landmaskJson.width, landmaskJson.height, landmaskJson.bits)
